import { trackEvent } from '@/api/track';
import { useUserStore } from '@/store/useUserStore';

export const CREDIT_GATE_VIEW_EVENT_CODE = 'credit_gate_view';
export const CREDIT_GATE_ACTION_EVENT_CODE = 'credit_gate_action';

export type CreditGateUiType = 'inline' | 'modal';
export type CreditGateAction = 'cancel' | 'close' | 'get_credits';

export type CreditsRequiredUiData = {
  productArea?: string;
  surface?: string;
  stage?: string;
  trigger?: string;
  uiType?: CreditGateUiType;
  source?: string;
  operation?: string;
  taskCode?: string | null;
  title?: string;
  copy?: string;
  targetRoute?: string;
  metadata?: Record<string, unknown>;
};

type CreditsRequiredUiActionData = CreditsRequiredUiData & {
  action: CreditGateAction;
};

const normalizeText = (value: unknown) => String(value ?? '').trim();

export const getCreditsRequiredUiRoute = () => {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const toSnakeCase = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();

const normalizeMetadataKey = (key: string) => {
  const snakeKey = toSnakeCase(key);
  if (snakeKey === 'action') return 'requested_action';
  return snakeKey;
};

const normalizeCreditGateMetadata = (metadata: Record<string, unknown> = {}) =>
  Object.entries(metadata).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (value === undefined) return acc;
    const normalizedKey = normalizeMetadataKey(key);
    if (!normalizedKey) return acc;
    acc[normalizedKey] = value;
    return acc;
  }, {});

const buildCreditsRequiredUiData = (data: CreditsRequiredUiData = {}) => ({
  ...normalizeCreditGateMetadata(data.metadata),
  route: getCreditsRequiredUiRoute(),
  product_area: normalizeText(data.productArea) || 'unknown',
  surface: normalizeText(data.surface) || 'unknown',
  stage: normalizeText(data.stage) || 'unknown',
  trigger: normalizeText(data.trigger) || 'unknown',
  ui_type: data.uiType || 'modal',
  source: normalizeText(data.source) || 'unknown',
  operation: normalizeText(data.operation),
  task_code: normalizeText(data.taskCode),
  title: normalizeText(data.title),
  copy: normalizeText(data.copy),
  is_authenticated: useUserStore.getState().isAuthenticated,
});

export const trackCreditsRequiredUiView = (data: CreditsRequiredUiData = {}) => {
  trackEvent({
    eventCode: CREDIT_GATE_VIEW_EVENT_CODE,
    eventData: JSON.stringify(buildCreditsRequiredUiData(data)),
  });
};

export const trackCreditsRequiredUiAction = (data: CreditsRequiredUiActionData) => {
  trackEvent({
    eventCode: CREDIT_GATE_ACTION_EVENT_CODE,
    eventData: JSON.stringify({
      ...buildCreditsRequiredUiData(data),
      action: data.action,
      target_route: normalizeText(data.targetRoute),
    }),
  });
};
